// ============================================================================
// SanteEquipePage — sante de mon equipe (L10, F-168..F-170) — /mon-equipe/sante
// ============================================================================
// Portage du PoC (dashboard)/mon-equipe/sante/page.tsx : meme garde (M-150 —
// director|admin, manager REFUSE) et meme portee (M-151) que le roster, servies
// par GET /api/mon-equipe/sante. Le board (TeamHealthBoard) consomme
// lib/member-pulse ; les check-ins sont en LECTURE seule (leur ECRITURE est
// L09/ManagementTab, hors lot).

import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, EmptyState, GsSegmented, SkeletonCard, QueryError, GS_COMPONENTS } from '@gs/ui';
import { Users, ArrowLeft } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useSanteEquipe } from '../../hooks/useMonEquipe';
import { TeamHealthBoard } from '../../components/mon-equipe/TeamHealthBoard';
import { MonEquipeForbidden } from './MonEquipePage';

export default function SanteEquipePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const teamParam = searchParams.get('team'); // F-168 : ?team=
  const q = useSanteEquipe(teamParam, !!user);
  const forbidden = q.isError && q.error instanceof ApiForbiddenError;

  function selectTeam(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('team', id);
    setSearchParams(next);
  }

  const data = q.data;
  const selectedTeam = data?.teams.find((team) => team.id === data.selectedTeamId) ?? null;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:sante.title')}
        description={selectedTeam?.name}
        onRefresh={() => void q.refetch()}
        refreshLoading={q.isFetching}
      />

      <Link
        to={selectedTeam ? `/mon-equipe?team=${encodeURIComponent(selectedTeam.id)}` : '/mon-equipe'}
        className="inline-flex touch-manipulation items-center gap-2 text-field text-slate-600 transition-colors hover:text-brand-600 dark:text-slate-300 dark:hover:text-brand-400"
      >
        <ArrowLeft size={16} aria-hidden="true" />
        {t('gest:sante.back')}
      </Link>

      {forbidden ? (
        // M-150 : meme refus que le roster — la sante n'est pas une porte derobee.
        <MonEquipeForbidden />
      ) : q.isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : q.isError ? (
        <QueryError message={t('gest:sante.loadError')} onRetry={() => void q.refetch()} />
      ) : data ? (
        <>
          {data.teams.length > 1 ? (
            <GsSegmented
              options={data.teams.map((team) => ({ value: team.id, label: team.name }))}
              value={data.selectedTeamId ?? data.teams[0].id}
              onChange={selectTeam}
              ariaLabel={t('gest:monEquipe.teamSelector')}
              variant="scrollable"
            />
          ) : null}

          {!selectedTeam ? (
            <EmptyState
              icon={<Users size={48} aria-hidden="true" />}
              title={t('gest:monEquipe.empty.title')}
              description={t('gest:monEquipe.empty.description')}
            />
          ) : (
            <div className={`${GS_COMPONENTS.card} p-5`}>
              <TeamHealthBoard roster={data.roster} pulses={data.pulses} checkins={data.checkins} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
